import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { useAuth } from '@/context/AuthContext'
import { useUpvoteStatus } from '@/hooks/useUpvoteStatus'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from '@/components/ui/dialog'
import { VisuallyHidden } from '@/components/ui/visually-hidden'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { format, formatDistanceToNow } from 'date-fns'
import {
	Calendar,
	Tag as TagIcon,
	X,
	Loader2,
	AlertTriangle,
	Share2,
	Copy,
	Twitter,
	Linkedin,
	Facebook,
	User as UserIcon,
	ChevronUp,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getInitials } from '@/utils/reviews'
import { toast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'
import {
	Tooltip,
	TooltipProvider,
	TooltipTrigger,
	TooltipContent,
} from '@/components/ui/tooltip'
import { PublicAuthModal } from './PublicAuthModal'
import { useTranslation } from '@/hooks/useTranslation'

// --- Types ---
interface Voter {
	created_at: string
	user_id: string | null
	user_identifier: string | null
	user: {
		id: string
		full_name?: string | null
		avatar_url?: string | null
	} | null
}

interface TagType {
	id: string
	name: string
	color?: string | null
}

interface FeatureDetail {
	id: string
	title: string
	description: string
	status: string
	upvote_count: number
	created_at: string
	submitter_name: string | null
	submitter_avatar_url: string | null
	submitter_email: string | null
	tags: TagType[]
	feature_request_upvotes: Voter[]
	column_name?: string | null
	column_color?: string | null
}

interface PublicFeatureDetailModalProps {
	isOpen: boolean
	onClose: () => void
	featureId: string | null
	teamSlug: string
}

// Helper function for status colors
const getStatusColorClasses = (status?: string) => {
	switch (status?.toLowerCase()) {
		case 'in review':
			return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800'
		case 'in progress':
			return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800'
		case 'completed':
		case 'launched':
			return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800'
		case 'planned':
			return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300 dark:border-purple-800'
		case 'declined':
		case 'rejected':
			return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800'
		default:
			return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
	}
}

// Helper function for status indicator color
const getStatusIndicatorColor = (status?: string) => {
	switch (status?.toLowerCase()) {
		case 'in review':
			return '#3b82f6'
		case 'in progress':
			return '#f59e0b'
		case 'completed':
		case 'launched':
			return '#10b981'
		case 'planned':
			return '#8b5cf6'
		case 'declined':
		case 'rejected':
			return '#ef4444'
		default:
			return '#64748b'
	}
}

// Share Dropdown Component
const ShareDropdown = ({
	title,
	featureId,
	teamSlug,
	isPortal,
}: {
	title: string
	featureId: string
	teamSlug: string
	isPortal: boolean
}) => {
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)

	// Construct the direct URL that will open the modal
	const baseUrl = window.location.origin
	const featureUrl = `${baseUrl}/roadmap?feature=${featureId}`

	const copyUrl = async () => {
		try {
			await navigator.clipboard.writeText(featureUrl)
			toast({
				title: t('publicFeatureDetailModal.urlCopied'),
				description: t('publicFeatureDetailModal.shareLinkDescription'),
			})
		} catch (error) {
			const textArea = document.createElement('textarea')
			textArea.value = featureUrl
			document.body.appendChild(textArea)
			textArea.focus()
			textArea.select()
			document.execCommand('copy')
			document.body.removeChild(textArea)
			toast({ title: t('publicFeatureDetailModal.urlCopied') })
		}
	}

	const socialShare = (platform: 'twitter' | 'facebook' | 'linkedin') => {
		let shareUrl = ''
		const shareText = t('publicFeatureDetailModal.checkOutFeatureRequest', { title })

		switch (platform) {
			case 'twitter':
				shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
					featureUrl
				)}&text=${encodeURIComponent(shareText)}`
				break
			case 'facebook':
				shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
					featureUrl
				)}`
				break
			case 'linkedin':
				shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(
					featureUrl
				)}&title=${encodeURIComponent(shareText)}`
				break
		}
		window.open(shareUrl, '_blank', 'noopener,noreferrer')
	}

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					size="lg"
					className="flex items-center gap-2 px-3 py-2 transition-all duration-200 hover:scale-105 hover:shadow-md">
					<Share2
						className={cn(
							'h-4 w-4 transition-transform duration-200',
							isOpen && 'rotate-12'
						)}
					/>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="end"
				className="w-48 animate-in slide-in-from-top-2 duration-200">
				<DropdownMenuItem onClick={copyUrl} className="cursor-pointer">
					<Copy className="mr-2 h-4 w-4" /> {t('publicFeatureDetailModal.copyUrl')}
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() => socialShare('twitter')}
					className="cursor-pointer">
					<Twitter className="mr-2 h-4 w-4" /> {t('publicFeatureDetailModal.twitter')}
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() => socialShare('facebook')}
					className="cursor-pointer">
					<Facebook className="mr-2 h-4 w-4" /> {t('publicFeatureDetailModal.facebook')}
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() => socialShare('linkedin')}
					className="cursor-pointer">
					<Linkedin className="mr-2 h-4 w-4" /> {t('publicFeatureDetailModal.linkedin')}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export const PublicFeatureDetailModal: React.FC<
	PublicFeatureDetailModalProps
> = ({ isOpen, onClose, featureId, teamSlug }) => {
	const { t } = useTranslation()
	const { isPortal } = useTeamSlug()
	const { session } = useAuth()
	const queryClient = useQueryClient()
	const [isUpvoteHovered, setIsUpvoteHovered] = useState(false)
	const [showAuthModal, setShowAuthModal] = useState(false)

	const {
		data: feature,
		isLoading,
		isError,
		error,
	} = useQuery<FeatureDetail, Error>({
		queryKey: ['publicFeature', featureId, isPortal],
		queryFn: () => {
			if (!featureId) throw new Error('Feature ID is required')

			if (isPortal) {
				return apiClient(`/api/portal/feature/${featureId}`, {
					needsAuth: false,
				})
			} else {
				return apiClient(`/api/public/features/${featureId}`, {
					needsAuth: false,
				})
			}
		},
		enabled: isOpen && !!featureId,
	})

	// Check upvote status for this feature
	const { data: upvoteStatusMap = {} } = useUpvoteStatus(
		featureId ? [featureId] : []
	)
	const hasUpvoted = upvoteStatusMap[featureId || ''] || false

	// Upvote mutation
	const upvoteMutation = useMutation({
		mutationFn: async (featureId: string) => {
			const startTime = Date.now()

			try {
				let result
				if (isPortal) {
					// Subdomain route - use the portal API
					result = await apiClient(`/api/portal/feature/${featureId}/upvote`, {
						method: 'POST',
						needsAuth: true,
					})
				} else {
					// Path-based route - use the legacy API
					result = await apiClient(`/api/public/features/${featureId}/upvote`, {
						method: 'POST',
						needsAuth: true,
					})
				}

				const duration = Date.now() - startTime

				return result
			} catch (error) {
				const duration = Date.now() - startTime
				console.error(
					'[FEATURE MODAL] Upvote mutation failed after',
					duration + 'ms',
					{
						featureId,
						error,
					}
				)
				throw error
			}
		},
		onSuccess: (data, featureId) => {
			// Optimistically update the feature upvote count
			if (feature) {
				// Fix: Handle both upvote and downvote cases properly
				let newUpvoteCount
				if (data?.upvoteCount !== undefined) {
					// Backend provided the new count
					newUpvoteCount = data.upvoteCount
				} else if (data?.upvoted !== undefined) {
					// Backend provided upvoted status - calculate based on current status
					if (data.upvoted) {
						// Adding upvote
						newUpvoteCount = feature.upvote_count + 1
					} else {
						// Removing upvote
						newUpvoteCount = Math.max(0, feature.upvote_count - 1)
					}
				} else {
					// Fallback - assume toggle based on current status
					if (hasUpvoted) {
						// Was upvoted, now removing
						newUpvoteCount = Math.max(0, feature.upvote_count - 1)
					} else {
						// Was not upvoted, now adding
						newUpvoteCount = feature.upvote_count + 1
					}
				}

				queryClient.setQueryData(['publicFeature', featureId, isPortal], {
					...feature,
					upvote_count: newUpvoteCount,
				})
			}

			// Update the upvote status cache - find the existing cache entry that contains this feature
			// The roadmap uses ['upvoteStatus', userId, allFeatureIds, isPortal]
			// We need to find and update that cache entry instead of creating a new one
			const queryCache = queryClient.getQueryCache()
			const upvoteStatusQueries = queryCache.findAll({
				queryKey: ['upvoteStatus'],
				exact: false,
			})

			// Find the query that contains our feature ID and matches our user/portal context
			const matchingQuery = upvoteStatusQueries.find((query) => {
				const [, userId, featureIds, portal] = query.queryKey
				return (
					userId === session?.user?.id &&
					portal === isPortal &&
					Array.isArray(featureIds) &&
					featureIds.includes(featureId)
				)
			})

			if (matchingQuery) {
				queryClient.setQueryData(
					matchingQuery.queryKey,
					(oldData: Record<string, boolean> | undefined) => {
						const currentStatus = oldData?.[featureId] || false
						return {
							...oldData,
							[featureId]: !currentStatus,
						}
					}
				)
			}

			const isUpvoted = data?.upvoted !== undefined ? data.upvoted : !hasUpvoted
			toast({
				title: isUpvoted ? t('publicFeatureDetailModal.featureUpvoted') : t('publicFeatureDetailModal.upvoteRemoved'),
				description: isUpvoted
					? t('publicFeatureDetailModal.thankYouFeedback')
					: t('publicFeatureDetailModal.upvoteRemovedDescription'),
			})

			// Invalidate related queries to refetch fresh data
			queryClient.invalidateQueries({
				queryKey: ['publicFeature', featureId, isPortal],
			})
			queryClient.invalidateQueries({
				queryKey: ['publicRoadmap', teamSlug, isPortal],
			})

			// Also invalidate upvote status queries to ensure consistency
			queryClient.invalidateQueries({
				queryKey: ['upvoteStatus'],
			})
		},
		onError: (error, featureId) => {
			console.error('[FEATURE MODAL] Upvote error:', { featureId, error })
			toast({
				variant: 'destructive',
				title: t('publicFeatureDetailModal.failedToUpvote'),
				description: t('publicFeatureDetailModal.pleaseTryAgainLater'),
			})
		},
	})

	const handleUpvote = () => {
		if (!session?.user) {
			setShowAuthModal(true)
			return
		}

		if (!featureId) return

		upvoteMutation.mutate(featureId)
	}

	const statusColors = feature ? getStatusColorClasses(feature.status) : null
	const statusIndicatorColor = feature
		? getStatusIndicatorColor(feature.status)
		: null

	return (
		<>
			<Dialog open={isOpen} onOpenChange={onClose}>
				<DialogContent className="max-w-4xl max-h-[90vh] w-[95vw] p-0 py-6 md:p-4 gap-0  border overflow-hidden sm:w-full animate-in fade-in-0 zoom-in-95 duration-200 rounded-xl">
					<VisuallyHidden>
						<DialogTitle>{t('publicFeatureDetailModal.featureRequestDetails')}</DialogTitle>
						<DialogDescription>
							{t('publicFeatureDetailModal.viewDetailedInfo')}
						</DialogDescription>
					</VisuallyHidden>
					{isLoading ? (
						<div className="flex items-center justify-center p-12 animate-in fade-in-0 duration-300">
							<div className="text-center">
								<Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
							</div>
						</div>
					) : isError || !feature ? (
						<div className="flex items-center justify-center p-12 animate-in fade-in-0 duration-300">
						<div className="text-center">
							<AlertTriangle className="h-8 w-8 mx-auto mb-4 text-destructive animate-in zoom-in-50 duration-200" />
							<h3 className="text-lg font-semibold mb-2">
								{t('publicFeatureDetailModal.failedToLoadFeature')}
							</h3>
							<p className="text-sm text-muted-foreground mb-4">
								{(error as any)?.status === 404
									? t('publicFeatureDetailModal.featureNotFound')
									: t('publicFeatureDetailModal.errorLoadingFeature')}
							</p>
							<Button
								onClick={onClose}
								variant="outline"
								className="transition-all duration-200 hover:scale-105">
								{t('publicFeatureDetailModal.close')}
							</Button>
						</div>
						</div>
					) : (
						<div className="grid grid-cols-1 lg:grid-cols-3 animate-in slide-in-from-bottom-4 duration-300">
							{/* Main Content */}
							<div className="lg:col-span-2 p-4 overflow-y-auto border m-3 rounded-lg">
								<div className="flex justify-between w-full items-center">
									<h1 className="text-xl font-semibold text-foreground leading-tight">
										{feature.title}
									</h1>
									<div className="flex items-center gap-2">
										<ShareDropdown
											title={feature.title}
											featureId={featureId!}
											teamSlug={teamSlug}
											isPortal={isPortal}
										/>
										<Button
											variant="outline"
											size="lg"
											disabled={upvoteMutation.isPending}
											className={cn(
												'relative w-fit rounded-xl cursor-pointer select-none px-3 py-2 transition-all duration-200 group/vote',
												'border backdrop-blur-sm hover:shadow-md',
												hasUpvoted
													? 'bg-primary/10 border-primary/30 hover:bg-primary/15 hover:border-primary/40'
													: 'bg-transparent border-border/50 hover:bg-primary/10 hover:border-primary/30',
												upvoteMutation.isPending &&
													'opacity-50 cursor-not-allowed'
											)}
											onClick={handleUpvote}
											onMouseEnter={() => setIsUpvoteHovered(true)}
											onMouseLeave={() => setIsUpvoteHovered(false)}>
											<div className="flex flex-row items-center justify-center gap-1 size-full">
												<div className="flex-shrink-0 transition-all duration-300 group-hover/vote:-translate-y-0.5">
													{upvoteMutation.isPending ? (
														<Loader2
															size={24}
															className="animate-spin text-muted-foreground"
														/>
													) : (
														<ChevronUp
															size={24}
															strokeWidth={3}
															className={cn(
																'transition-all duration-300',
																hasUpvoted
																	? 'text-primary'
																	: isUpvoteHovered
																	? 'text-primary transform scale-110'
																	: 'text-muted-foreground group-hover/vote:text-primary'
															)}
														/>
													)}
												</div>
												<span
													className={cn(
														'text-sm font-semibold transition-all duration-300',
														hasUpvoted
															? 'text-primary'
															: isUpvoteHovered
															? 'text-primary scale-105'
															: 'text-foreground group-hover/vote:text-primary'
													)}>
													{feature.upvote_count}
												</span>
											</div>
										</Button>
									</div>
								</div>
								<Separator className="my-4" />
								<div className="prose prose-slate dark:prose-invert max-w-none">
									<p className="text-muted-foreground leading-relaxed text-base whitespace-pre-wrap">
										{feature.description}
									</p>
								</div>
							</div>

							{/* Sidebar */}
							<div className="h-min w-full p-4 space-y-3">
								{/* Info Card */}
								<div className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl border shadow-sm p-4 space-y-4 animate-in slide-in-from-right-4 duration-300 delay-100">
									{/* Author */}
									<div className="flex items-center gap-3 w-full ">
										<span className="text-xs font-semibold text-muted-foreground min-w-[70px] lg:text-sm">
											{t('publicFeatureDetailModal.author')}
										</span>
										<div className="flex items-center gap-2 min-h-[20px] overflow-hidden rounded-full">
											<div className="h-5 w-5 overflow-hidden rounded-full border border-border/30">
												<Avatar className="h-full w-full">
													{feature.submitter_avatar_url ? (
														<AvatarImage
															src={feature.submitter_avatar_url}
															alt={t('publicFeatureDetailModal.authorAvatar')}
															className="object-cover"
														/>
													) : null}
													<AvatarFallback className="text-xs bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 font-medium">
														{getInitials(feature.submitter_name)}
													</AvatarFallback>
												</Avatar>
											</div>
											<span className="text-sm  text-foreground truncate max-w-[120px]">
												{feature.submitter_name || t('publicFeatureDetailModal.anonymous')}
											</span>
										</div>
									</div>

									<div className="flex items-center gap-3 w-full">
										<span className="text-xs font-semibold text-muted-foreground min-w-[70px] lg:text-sm">
											{t('publicFeatureDetailModal.created')}
										</span>

										<TooltipProvider>
											<Tooltip delayDuration={0}>
												<TooltipTrigger asChild>
													<span className="text-xs  w-full lg:text-sm  text-foreground whitespace-nowrap">
														{formatDistanceToNow(new Date(feature.created_at), {
															addSuffix: true,
														})}
													</span>
												</TooltipTrigger>
												<TooltipContent>
													{format(new Date(feature.created_at), 'PPpp')}
												</TooltipContent>
											</Tooltip>
										</TooltipProvider>
									</div>
								</div>

								{/* Meta Card */}
								<div className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl border  shadow-sm p-4 space-y-4 animate-in slide-in-from-right-4 duration-300 delay-150">
									<div className="flex items-center gap-3 w-full ">
										<span className="text-xs font-semibold text-muted-foreground min-w-[70px] lg:text-sm pt-0.5">
											{t('publicFeatureDetailModal.status')}
										</span>
										<div className="bg-white/80 dark:bg-slate-800/80 rounded-lg px-3 py-1 border shadow-sm  ">
											<div className="flex items-center gap-2">
												<Tooltip delayDuration={0}>
													<TooltipTrigger className="max-w-[120px] truncate">
														<span
															className="text-xs lg:text-sm  whitespace-nowrap select-none max-w-[120px] truncate"
															style={{
																color:
																	feature.column_color || statusIndicatorColor,
															}}>
															{feature.status}
														</span>
													</TooltipTrigger>
													<TooltipContent className="mb-2">
														{feature.status}
													</TooltipContent>
												</Tooltip>
											</div>
										</div>
									</div>

									{feature.tags.length > 0 && (
										<div className="flex items-start gap-3 w-full">
											<span className="text-xs font-semibold text-muted-foreground min-w-[70px] lg:text-sm pt-0.5">
												{t('publicFeatureDetailModal.tags')}
											</span>
											<div className="flex flex-wrap gap-1.5">
												{feature.tags.map((tag) => (
													<div
														key={tag.id}
														className="bg-white/80 dark:bg-slate-800/80 rounded-lg px-3 py-1 border shadow-sm flex items-center gap-2 transition-all duration-200 hover:scale-105"
														style={{
															borderColor: `${tag.color || '#10b981'}80`,
														}}>
														<span
															className="text-xs  capitalize select-none lg:text-sm"
															style={{ color: tag.color || '#10b981' }}>
															{tag.name}
														</span>
													</div>
												))}
											</div>
										</div>
									)}
								</div>

								<div className="bg-white/80 dark:bg-slate-800/60 backdrop-blur-sm rounded-xl border  shadow-sm p-4 animate-in slide-in-from-right-4 duration-300 delay-200">
									<div className="flex flex-col gap-3">
										<div className="flex gap-3 w-full">
											<span className="text-xs font-semibold text-muted-foreground min-w-[70px] lg:text-sm pt-0.5">
												{t('publicFeatureDetailModal.voters')}
											</span>
											<div className="flex items-center flex-wrap gap-2 w-full pt-0.5 transition-all">
												{feature.feature_request_upvotes
													.slice(0, 12)
													.map((voter, index) => (
														<TooltipProvider
															key={
																voter.user_id || voter.user_identifier || index
															}>
															<Tooltip delayDuration={0}>
																<TooltipTrigger asChild>
																	<div className="flex items-center gap-2 h-min overflow-hidden rounded-full">
																		<div className="h-5 w-5 overflow-hidden rounded-full border border-border/30">
																			<Avatar className="h-full w-full">
													{voter.user?.avatar_url ? (
														<AvatarImage
															src={voter.user.avatar_url}
															alt={t('publicFeatureDetailModal.voterAvatar')}
															className="object-cover"
														/>
													) : null}
													<AvatarFallback className="text-xs bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 font-medium">
														{voter.user?.full_name
															? getInitials(voter.user.full_name)
															: '?'}
													</AvatarFallback>
												</Avatar>
											</div>
										</div>
									</TooltipTrigger>
									<TooltipContent>
										<div className="text-center">
											<div className="font-medium">
												{voter.user?.full_name ||
													t('publicFeatureDetailModal.anonymousUser')}
											</div>
																		<div className="text-xs text-muted-foreground mt-1">
																			{format(
																				new Date(voter.created_at),
																				'PPp'
																			)}
																		</div>
																	</div>
																</TooltipContent>
															</Tooltip>
														</TooltipProvider>
													))}
												{feature.feature_request_upvotes.length > 12 && (
													<div className="h-5 w-5 bg-muted rounded-full flex items-center justify-center border border-border/30">
														<span className="text-xs text-muted-foreground font-medium">
															+{feature.feature_request_upvotes.length - 12}
														</span>
													</div>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<PublicAuthModal
				isOpen={showAuthModal}
				onClose={() => setShowAuthModal(false)}
				onSuccess={() => {
					setShowAuthModal(false)
					// Optionally retry the upvote after successful auth
					if (featureId) {
						upvoteMutation.mutate(featureId)
					}
				}}
			/>
		</>
	)
}
